/*
  ExerciseProgramA4Preview — live in-editor A4 preview of a program.

  Mirrors the Weasyprint template served by
  GET /api/exercise-programs/{id}/pdf so the practitioner sees the
  client-facing output update as they type. Scaled to fit the editor
  side panel; toggleable so it can be hidden on smaller screens.
*/

const DOSE_FIELDS = [
  ["sets", "Sets"], ["reps", "Reps"], ["hold", "Hold"], ["load", "Load"],
  ["band", "Band"], ["spring", "Spring"], ["apparatus", "Apparatus"],
  ["tempo", "Tempo"], ["rest", "Rest"], ["rpe", "RPE"],
  ["frequency", "Frequency"], ["side", "Side"],
];

export default function ExerciseProgramA4Preview({ program, programId }) {
  if (!program) return null;
  const review = program.review_date;
  const issued = (program.updated_at || "").slice(0, 10);
  const practitioner = program.primary_practitioner?.name || "";

  return (
    <div
      className="a4-preview-wrap bg-zinc-100 h-full overflow-auto p-3"
      data-testid="ep-a4-preview"
    >
      <div
        className="mx-auto bg-white shadow-md text-2xs leading-tight text-zinc-900"
        style={{
          width: "210mm",
          minHeight: "297mm",
          padding: "18mm 15mm",
          transform: "scale(0.6)",
          transformOrigin: "top center",
          marginBottom: "-40%",  // compensate for the scaled-down height
          boxSizing: "border-box",
        }}
      >
        <h1 className="text-[20pt] leading-tight m-0 mb-1 font-sans" style={{ fontWeight: 700 }}>
          {program.title || "Exercise program"}
        </h1>
        <div className="text-[9pt] text-slate-600 mb-3">
          {practitioner && <>Prescribed by {practitioner} · </>}
          Version {program.version || 1}
          {issued && <> · Issued {issued}</>}
          {review && <> · Review by {review.slice(0, 10)}</>}
        </div>

        <PainInlineRow program={program} kind="pre" />

        {(program.sections || []).length === 0 ? (
          <p className="italic text-slate-500 text-[10pt]">No exercises prescribed yet.</p>
        ) : (
          (program.sections || []).map((s, si) => (
            <div key={si} className="mb-3">
              <h2
                className="text-[14pt] mt-3 mb-1 pb-1"
                style={{
                  color: "#3730a3",
                  borderBottom: "2px solid #6366f1",
                  fontWeight: 600,
                }}
              >
                {s.label || s.key || "Section"}
              </h2>
              {s.narrative && (
                <p className="text-slate-700 text-[10pt] mb-2 whitespace-pre-wrap">
                  {s.narrative}
                </p>
              )}
              {(s.exercises || []).length === 0 && (
                <p className="text-slate-400 italic text-[9pt]">No exercises in this section yet.</p>
              )}
              {(s.exercises || []).map((ex, ei) => {
                const setGroups = (ex.set_groups || []).filter(
                  (g) => g && (g.sets || g.reps || g.load),
                );
                let parts;
                if (setGroups.length > 0) {
                  // Prepend the flat sets/reps/load row when it
                  // isn't already the first set-group so a practitioner
                  // who fills both flat and breakdown sees both lines.
                  const flatSets = (ex.sets || "").toString().trim();
                  const flatReps = (ex.reps || "").toString().trim();
                  const flatLoad = (ex.load || "").toString().trim();
                  const first = setGroups[0] || {};
                  const dupFirst = flatSets === (first.sets || "").toString().trim()
                    && flatReps === (first.reps || "").toString().trim()
                    && flatLoad === (first.load || "").toString().trim();
                  const rows = [];
                  if ((flatSets || flatReps || flatLoad) && !dupFirst) {
                    rows.push({ sets: flatSets, reps: flatReps, load: flatLoad });
                  }
                  rows.push(...setGroups);
                  const summary = rows
                    .map((g) => {
                      const left = g.sets && g.reps ? `${g.sets}\u00d7${g.reps}` : (g.sets || g.reps || "");
                      return g.load ? `${left} @ ${g.load}` : left;
                    })
                    .filter(Boolean)
                    .join("  /  ");
                  parts = [
                    <span key="sb" className="mr-2 text-slate-700 text-[10pt]">{summary}</span>,
                    ...DOSE_FIELDS
                      .filter(([k]) => !["sets", "reps", "load"].includes(k) && ex[k])
                      .map(([k, lbl]) => (
                        <span key={k} className="mr-2 text-slate-700 text-[10pt]">
                          <b>{lbl}:</b> {String(ex[k])}
                        </span>
                      )),
                  ];
                } else {
                  parts = DOSE_FIELDS
                    .filter(([k]) => ex[k])
                    .map(([k, lbl]) => (
                      <span key={k} className="mr-2 text-slate-700 text-[10pt]">
                        <b>{lbl}:</b> {String(ex[k])}
                      </span>
                    ));
                }
                return (
                  <div
                    key={ex.id || ei}
                    className="flex gap-3 items-start py-2"
                    style={{ borderBottom: "1px solid #e2e8f0", pageBreakInside: "avoid" }}
                  >
                    {ex.image_rel_id && programId && (
                      <img
                        src={`/api/exercise-programs/${programId}/image/${ex.image_rel_id}`}
                        alt=""
                        className="border border-slate-300 rounded"
                        style={{ width: "110pt", maxHeight: "150pt", objectFit: "contain" }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[12pt]">{ex.name || "—"}</div>
                      {parts.length > 0 && (
                        <div className="text-slate-700 text-[10pt] mt-0.5">{parts}</div>
                      )}
                      {ex.instructions && (
                        <div className="text-slate-600 text-[10pt] mt-1 whitespace-pre-wrap">
                          {ex.instructions}
                        </div>
                      )}
                      {ex.precautions && (
                        <div
                          className="text-[10pt] mt-1 rounded px-1 py-0.5"
                          style={{ color: "#b45309", background: "#fef3c7" }}
                        >
                          <b>Precaution:</b> {String(ex.precautions)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}

        <PainInlineRow program={program} kind="post" />

        <footer
          className="text-[9pt] text-slate-500 mt-4 pt-2"
          style={{ borderTop: "1px solid #e2e8f0" }}
        >
          Please follow the prescribed dosage. Contact your practitioner if
          you experience new or worsening pain.
        </footer>
      </div>
    </div>
  );
}


/* Single-line hand-fill pain row for the compact client-facing PDF.
   Since the compact layout has no day-column grid, we render one
   underlined blank at the top (pre-session) and one at the bottom
   (post-session) — mirroring the worksheet's Pre / Post rows in
   spirit while keeping the client-facing document tidy. */
function PainInlineRow({ program, kind }) {
  if (!program?.pain_tracking_enabled) return null;
  const label = kind === "pre" ? "Pre-session pain" : "Post-session pain";
  const tone = kind === "pre"
    ? { bg: "#fff1f2", fg: "#9f1239" }
    : { bg: "#f0fdf4", fg: "#065f46" };
  return (
    <div
      className="flex items-baseline gap-2 my-2 px-2 py-1 rounded border text-[10pt]"
      style={{ background: tone.bg, borderColor: tone.fg }}
      data-testid={`ep-compact-preview-pain-${kind}`}
    >
      <span
        className="uppercase tracking-wide text-[9pt] font-bold"
        style={{ color: tone.fg }}
      >
        {label} (0-10):
      </span>
      <span className="font-mono">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
      <span className="text-slate-500 text-[9pt]">
        (0 = no pain, 10 = worst imaginable)
      </span>
    </div>
  );
}
